import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useToast } from '@/composables/useToast';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { AiSmartActionStatus, useSmartActionsStore } from '@/modules/viewer/store/useSmartActionsStore';
import { useAiMetadataStore } from '@/modules/viewer/store/useAiMetadataStore';
import { useTranscriptionStore } from '@/modules/viewer/store/useTranscriptionStore';
import { useExportUtils } from '@/modules/viewer/composables/useExportUtils';
import { DEFAULT_AGENTS, useUserStore, type DefaultAgent } from '@/store/useUserStore';
import { useSettingsStore } from '@/store/useSettingsStore';

export const AGENT_URLS = { claude: 'https://claude.ai/new', chatgpt: 'https://chatgpt.com/' } as const;
export const AGENT_NAMES = { claude: 'Claude', chatgpt: 'ChatGPT' } as const;
// Agents cut off or reject longer links. Past this the agent opens empty and the
// prompt goes by paste.
export const MAX_PREFILL_URL_LENGTH = 8000;
export const SMART_ACTION_PROMPT_INSTRUCTION = 'Use this Smart Action output from Zight as context.';
export const GUIDE_PROMPT_INSTRUCTION = 'Follow this step-by-step guide recorded with Zight. Each step links to a screenshot of that step.';
export const STEP_BY_STEP_GUIDE = 'step_by_step_guide';

type LinkAgent = keyof typeof AGENT_URLS;

export const useAgentHandoff = () => {
  const userStore = useUserStore();
  const { error, success } = useToast();
  const { description, displayName, hideBranding } = storeToRefs(useViewerItemStore());
  const { mcpServerUrl, mcpSetupUrl } = storeToRefs(useSettingsStore());
  const { actionType, actionStatus, isProcessing: isSmartActionProcessing } = storeToRefs(useSmartActionsStore());
  const { isProcessing: isAiMetadataProcessing } = storeToRefs(useAiMetadataStore());
  const { isTranscriptionProcessing } = storeToRefs(useTranscriptionStore());
  const { copyHtmlFragment, getVideoLink } = useExportUtils(description, displayName);

  const isSending = ref(false);
  const defaultAgent = computed<DefaultAgent>(() => {
    const saved = userStore.defaultAgent;
    return saved && DEFAULT_AGENTS.includes(saved) ? saved : 'claude';
  });
  // Same processing state the Summary panel shows its "working on" banner for, so the
  // control never offers output that is still generating or regenerating.
  const isGenerating = computed(() => isSmartActionProcessing.value || isAiMetadataProcessing.value || isTranscriptionProcessing.value);
  const canSendToAgent = computed(
    () => !!actionType?.value && actionStatus?.value === AiSmartActionStatus.DONE && !!description.value?.trim() && !isGenerating.value
  );

  // A guide's steps carry screenshot links, so its instruction tells the agent to follow them.
  const promptInstruction = computed(() => (actionType?.value === STEP_BY_STEP_GUIDE ? GUIDE_PROMPT_INSTRUCTION : SMART_ACTION_PROMPT_INSTRUCTION));
  // The export helper appends the video share link to both clipboard flavours.
  const prompt = computed(() => `${promptInstruction.value}\n\n${description.value ?? ''}`);

  const copyPrompt = () => copyHtmlFragment(prompt.value, { plainText: prompt.value });

  // Only agents get the MCP line. A clipboard copy may be pasted into a doc or ticket.
  const mcpConnectInstruction = computed(() => {
    if (hideBranding.value || !mcpServerUrl.value) {
      return null;
    }
    return `If the Zight MCP (${mcpServerUrl.value}) isn't connected, ask me to connect it so you can read this video's transcript, chapters and on-screen text. Setup guide: ${mcpSetupUrl.value}`;
  });

  const handoffText = computed(() => [prompt.value, getVideoLink(), mcpConnectInstruction.value].filter(Boolean).join('\n\n'));

  // Non-Latin text encodes to several characters per letter, so translated guides
  // reach the limit far sooner than English ones.
  const prefillUrl = (agent: LinkAgent) => {
    const url = `${AGENT_URLS[agent]}?q=${encodeURIComponent(handoffText.value)}`;
    return url.length <= MAX_PREFILL_URL_LENGTH ? url : null;
  };

  // A prefilled link opens inside the click. Output too long for the link is copied
  // first, as plain text, and the tab opens after the copy: once the new tab takes focus
  // the browser rejects clipboard writes. The tab relies on the click still counting as
  // a user action (about 1 s in Safari), so the copy skips image inlining to stay fast.
  const openAgent = async (agent: LinkAgent) => {
    const url = prefillUrl(agent);
    if (url) {
      window.open(url, '_blank', 'noopener');
      return;
    }
    await navigator.clipboard.writeText(handoffText.value);
    window.open(AGENT_URLS[agent], '_blank', 'noopener');
    success(`Prompt copied. Paste it into ${AGENT_NAMES[agent]}.`);
  };

  const rememberDefault = async (agent: DefaultAgent) => {
    if (agent === defaultAgent.value) {
      return;
    }
    try {
      await userStore.setDefaultAgent(agent);
    } catch {
      // The handoff already happened; the preference is saved again on the next pick.
    }
  };

  const sendTo = async (agent: DefaultAgent) => {
    if (!canSendToAgent.value || isSending.value) {
      return;
    }

    isSending.value = true;
    try {
      if (agent === 'clipboard') {
        await copyPrompt();
      } else {
        await openAgent(agent);
      }
      await rememberDefault(agent);
    } catch {
      error('Could not copy the Smart Action prompt');
    } finally {
      isSending.value = false;
    }
  };

  const sendToDefault = () => sendTo(defaultAgent.value);

  return {
    canSendToAgent,
    defaultAgent,
    isSending,
    sendTo,
    sendToDefault,
  };
};
